import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Laptop,
  Globe,
  ShoppingCart,
  Zap,
  Cpu,
  Palette,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  PhoneCall,
  Check,
  Clock,
  ChevronDown,
  Layers,
  Code2,
  Rocket,
  Search,
  MessageSquare,
  Smartphone,
  Star,
  Bell,
  Wifi,
  BatteryCharging,
  Download,
  ExternalLink,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CinematicDataCounterBackdrop from "../components/CinematicDataCounterBackdrop";
import { useModals } from "../contexts/ModalContext";

export const WEBDESIGN_PACKAGES = [
  {
    id: "landingpage",
    category: "Landing-Page",
    name: "Landing-Page & Onepager",
    badge: "Lead-Magnet",
    badgeClass: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    price: 590,
    priceLabel: "Einmalig ab CHF 590.-",
    deliveryTime: "5 - 7 Werktage",
    shortDesc: "Konversionsstarker Single-Page Webauftritt für Produktlaunches, Google-Ads Kampagnen, KMU & Startups.",
    icon: Laptop,
    iconColor: "text-cyan-400",
    features: [
      "1x Massgeschneiderte One-Page Website",
      "100% Responsive für Mobile, Tablet & Desktop",
      "Conversion-starke Hero-Section & CTA-Buttons",
      "Kontaktformular mit E-Mail-Weiterleitung",
      "Basis SEO-Optimierung & Google Indexierung",
      "DSG-konforme Schweizer Datenschutzerklärung",
      "Integration von Google Analytics oder Plausible",
      "Ladezeit-Optimierung (PageSpeed 90+)",
    ],
  },
  {
    id: "corporate",
    category: "Corporate Website",
    name: "KMU & Corporate Website",
    badge: "Bestseller KMU",
    badgeClass: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    price: 1490,
    priceLabel: "Einmalig ab CHF 1'490.-",
    deliveryTime: "10 - 14 Werktage",
    isFeatured: true,
    shortDesc: "Repräsentativer mehrseitiger Firmenauftritt mit individuellem Design, modernem CMS, Team-Bereich & Blog.",
    icon: Globe,
    iconColor: "text-amber-400",
    features: [
      "Bis zu 7 individuell gestaltete Unterseiten",
      "Einfaches Content-Management (CMS) zum Selberpflegen",
      "Interaktive Google Maps, Kontakt & Offert-Formular",
      "Umfassende On-Page SEO (Meta-Tags, Sitemap, Schema)",
      "Team-, Über-Uns- und Karriere-Bereich",
      "News / Blog-Modul für aktuelle Firmenberichte",
      "100% Swiss DSG konform inkl. Cookie-Banner",
      "1 Jahr kostenlose Hosting- & Domain-Aufschaltung",
    ],
  },
  {
    id: "ecommerce",
    category: "Online-Shop",
    name: "E-Commerce & Online-Shop",
    badge: "TWINT Ready",
    badgeClass: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    price: 2290,
    priceLabel: "Einmalig ab CHF 2'290.-",
    deliveryTime: "2 - 3 Wochen",
    shortDesc: "Verkaufen Sie Schweizer Produkte online. Mit WooCommerce oder Shopify, TWINT, Kreditkarte und automatischer Rechnungsstellung.",
    icon: ShoppingCart,
    iconColor: "text-emerald-400",
    features: [
      "Vollwertiger Schweizer Webshop (WooCommerce / Shopify)",
      "Anbindung Schweizer Zahlungsarten: TWINT, Stripe, QR-Rechnung",
      "Produktkatalog, Kategorien & Filteroptionen",
      "Automatische Schweizer MwSt-Berechnung (8.1% / 2.6%)",
      "Versandkosten-Matrix (Schweizer Post, PostPac Priority)",
      "Automatischer PDF-Rechnungsversand per E-Mail",
      "Gutschein-System & Rabatt-Aktionen",
      "Schulung & Einführung für Sie und Ihr Team",
    ],
  },
  {
    id: "redesign",
    category: "Redesign & Speed",
    name: "WordPress & Redesign",
    badge: "PageSpeed 95+",
    badgeClass: "bg-purple-500/20 text-purple-300 border-purple-500/40",
    price: 990,
    priceLabel: "Einmalig ab CHF 990.-",
    deliveryTime: "7 - 10 Werktage",
    shortDesc: "Frischer Look, blitzschnelle Ladezeiten und mobile Perfektion für Ihre bestehende Website ohne Datenverlust.",
    icon: Zap,
    iconColor: "text-purple-400",
    features: [
      "Modernisierung von veraltetem Design & Typografie",
      "Mobile-First Responsive Korrektur aller Unterseiten",
      "LiteSpeed Cache & Bildkomprimierung (WebP/AVIF)",
      "PageSpeed 95+ Score Garantie auf Google",
      "Beseitigung von Sicherheitslücken & veralteten Plugins",
      "Beibehaltung aller bestehenden Google-Rankings (301-Redirects)",
      "Bereinigung der WordPress-Datenbank",
      "Umzug auf Schweizer NVMe Server ohne Ausfallzeit",
    ],
  },
  {
    id: "webapp",
    category: "Web-Applikation",
    name: "Individuelle Web-Applikation",
    badge: "Full-Stack Custom",
    badgeClass: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    price: 2990,
    priceLabel: "Einmalig ab CHF 2'990.-",
    deliveryTime: "3 - 6 Wochen",
    shortDesc: "Kundenspezifische Softwarelösungen, Kundenportale, Buchungssysteme, Datenbanken, API-Schnittstellen und SaaS.",
    icon: Cpu,
    iconColor: "text-blue-400",
    features: [
      "Individuelle Entwicklung mit React, Next.js & FastAPI / Node.js",
      "Benutzerverwaltung mit Rollen (Admin, Mitarbeiter, Kunde)",
      "PostgreSQL / MySQL Datenbankarchitektur",
      "REST- & GraphQL-APIs für ERP-, CRM- oder Buchhaltungs-Anbindung",
      "Interaktive Dashboards & Daten-Visualisierungen",
      "Automatisierte E-Mail- & SMS-Benachrichtigungen",
      "Höchste Sicherheitsstandards (OWASP, 2FA, JWT Auth)",
      "Vollständige Dokumentation & Code-Übergabe",
    ],
  },
  {
    id: "branding",
    category: "Design & Identity",
    name: "UI/UX Design & Branding",
    badge: "Design Studio",
    badgeClass: "bg-pink-500/20 text-pink-300 border-pink-500/40",
    price: 490,
    priceLabel: "Einmalig ab CHF 490.-",
    deliveryTime: "5 - 7 Werktage",
    shortDesc: "Vektorbasiertes Logo-Design, Corporate Identity, Design-Systeme, Figma-Klickdummies & Markenrichtlinien.",
    icon: Palette,
    iconColor: "text-pink-400",
    features: [
      "3 individuelle Logo-Konzepte zur Auswahl",
      "Unbegrenzte Korrekturschleifen bis zur Freigabe",
      "Vektor-Formate (SVG, EPS, PDF, PNG hochauflösend)",
      "Farbpalette & Typografie-Definition (Brand Styleguide)",
      "Social-Media Banner & Favicon-Set",
      "Visitenkarten- & Briefpapier-Designvorlage",
      "Klickbarer Figma UI/UX High-Fidelity Prototyp",
      "Vollständige Übertragung aller Nutzungsrechte",
    ],
  },
  {
    id: "wartung",
    category: "SLA Support",
    name: "Website-Wartung & Support",
    badge: "SLA Flatrate",
    badgeClass: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    price: 79,
    priceLabel: "Ab CHF 79.- / Monat",
    deliveryTime: "Laufende Betreuung",
    shortDesc: "Wöchentliche Sicherheits-Updates, tägliche Backups, 24/7 Uptime-Monitoring und Schweizer Notfall-Support.",
    icon: ShieldCheck,
    iconColor: "text-sky-400",
    features: [
      "Wöchentliche CMS-, Theme- und Plugin-Updates",
      "Tägliche externe Datensicherung (Schweizer Cloud)",
      "24/7 Uptime- & Verfügbarkeits-Monitoring",
      "Imunify360 Malware-Scan & Web-Firewall",
      "Inklusive 1 Arbeitsstunde monatlich für Content-Anpassungen",
      "Prioritärer Schweizer Telefon- & Ticket-Support",
      "Garantierte Reaktionszeit unter 2 Stunden im Ernstfall",
      "Monatlicher Wartungs- & Sicherheitsbericht",
    ],
  },
];

export const WEBDESIGN_ADDONS = [
  {
    id: "express-launch",
    name: "48h Express-Launch",
    badge: "EIL-SERVICE",
    badgeColor: "bg-red-500/20 text-red-300 border-red-500/30",
    price: "CHF 390.-",
    priceSub: "einmalig",
    desc: "Wenn es besonders schnell gehen muss: Prioritäre Schweizer Entwickler-Ressourcen für Fertigstellung innert 48 Stunden.",
    features: ["Dedizierter Entwickler sofort", "Prioritäts-Freigabeprozess", "Garantierte Termintreue"],
    icon: Rocket,
  },
  {
    id: "multilingual",
    name: "4-Sprachigkeit (DE / FR / IT / EN)",
    badge: "SCHWEIZWEIT",
    badgeColor: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    price: "Ab CHF 490.-",
    priceSub: "pro Sprache",
    desc: "Erreichen Sie die ganze Schweiz mit professioneller Mehrsprachigkeit (WPML / Polylang) & flüssigem Sprachwechsler.",
    features: ["Saubere hreflang-Tags", "Übersetzte Menüs & URLs", "Automatische Spracherkennung"],
    icon: Globe,
  },
  {
    id: "seo-copy",
    name: "Schweizer SEO-Copywriting",
    badge: "CONTENT",
    badgeColor: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    price: "Ab CHF 290.-",
    priceSub: "pro Seite",
    desc: "Verkaufsstarke Webtexte verfasst von Schweizer Textern, perfekt optimiert auf kaufkräftige Google-Suchbegriffe.",
    features: ["Keyword-Recherche inklusive", "Verkaufspsychologische Struktur", "Meta-Tags & Teasertexte"],
    icon: Search,
  },
  {
    id: "google-business",
    name: "Google Business & Local SEO",
    badge: "LOCAL RANKING",
    badgeColor: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    price: "CHF 190.-",
    priceSub: "einmalig",
    desc: "Optimierung Ihres Google Maps Eintrags, lokale Sichtbarkeit für Ihre Region in der Schweiz & Rezensionen-Widget.",
    features: ["Google Unternehmensprofil", "Local Schema.org Markups", "Rezensionen-Badge auf Website"],
    icon: Star,
  },
  {
    id: "ndsg-compliance",
    name: "nDSG & Cookiebot Komplettpaket",
    badge: "RECHTSSICHER",
    badgeColor: "bg-purple-500/20 text-purple-300 border-purple-500/30",
    price: "CHF 240.-",
    priceSub: "einmalig",
    desc: "100% Konformität mit dem neuen Schweizer Datenschutzgesetz (nDSG), DSGVO-Audit & rechtssicherer Cookiebot.",
    features: ["Rechtssichere Datenschutzerklärung", "Opt-in Cookie-Banner", "Audit externer Skripte"],
    icon: ShieldCheck,
  },
  {
    id: "whatsapp-chat",
    name: "WhatsApp Live-Chat Integration",
    badge: "KONVERTIERUNG",
    badgeColor: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    price: "CHF 150.-",
    priceSub: "einmalig",
    desc: "Direkter Kundendialog über unaufdringlichen WhatsApp Business Button mit vordefinierten Begrüssungstexten.",
    features: ["WhatsApp Business API Ready", "Mobile Quick-Tap Button", "Automatischer Begrüssungstext"],
    icon: MessageSquare,
  },
  {
    id: "newsletter-auto",
    name: "Newsletter & Lead-Automation",
    badge: "LEAD GENERATION",
    badgeColor: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
    price: "CHF 290.-",
    priceSub: "einmalig",
    desc: "Anbindung an Mailchimp, Brevo oder ActiveCampaign samt automatisierter Willkommens-Sequenz & Double-Opt-In.",
    features: ["DSG-konformes Anmeldeformular", "Willkommens-E-Mail Template", "Listen-Segmentierung"],
    icon: Sparkles,
  },
  {
    id: "stock-photos",
    name: "Premium Bildrecherche & Grafikpaket",
    badge: "BRANDING",
    badgeColor: "bg-pink-500/20 text-pink-300 border-pink-500/30",
    price: "CHF 190.-",
    priceSub: "einmalig",
    desc: "Hochauflösende, lizenzfreie Stockfotos & massgeschneiderte Vektor-Grafiken abgestimmt auf Ihre Corporate Identity.",
    features: ["Bis zu 15 High-Res Lizenzbilder", "Farb- & Tonwertkorrektur", "WebP / AVIF Speed-Kompression"],
    icon: Palette,
  },
];

export default function WebdesignLanding() {
  const [searchParams] = useSearchParams();
  const selectedParam = searchParams.get("package");
  const [activeTab, setActiveTab] = useState(selectedParam || "all");
  const [activeFaq, setActiveFaq] = useState(null);
  const { openQuote, openContact } = useModals();

  useEffect(() => {
    if (selectedParam) {
      setActiveTab(selectedParam);
      const el = document.getElementById(selectedParam);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedParam]);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 200);
      }
    }
  }, []);

  const filteredPackages =
    activeTab === "all"
      ? WEBDESIGN_PACKAGES
      : WEBDESIGN_PACKAGES.filter((p) => p.id === activeTab);

  const faqs = [
    {
      q: "Wie läuft die Zusammenarbeit für ein Webdesign-Projekt ab?",
      a: "Wir starten mit einem unverbindlichen Beratungsgespräch, in dem wir Ihre Wiele, Zielgruppe und Design-Wünsche erfassen. Anschliessend erstellen wir ein massgeschneidertes Konzept samt Figma-Entwurf. Nach Ihrer Freigabe setzen wir die Website pixelgenau und mobiloptimiert um. Vor dem Go-Live testen wir alle Funktionen und schulen Sie auf Wunsch im CMS.",
    },
    {
      q: "Kann ich Inhalte, Texte und Bilder später selbst ändern?",
      a: "Ja, absolut! Wir bauen Websites mit intuitiven Content-Management-Systemen (wie WordPress, Elementor oder modernen Headless-CMS). Sie erhalten eine verständliche Einführung und können Texte, Bilder oder neue Blogartikel kinderleicht und ohne Programmierkenntnisse anpassen.",
    },
    {
      q: "Sind die Websites für Google (SEO) optimiert?",
      a: "Ja. Jede von uns erstellte Website wird technisch für Google optimiert: Saubere Überschriftenstrukturen (H1-H6), blitzschnelle Ladezeiten (PageSpeed 90+), Meta-Tags, OpenGraph-Bilder für Social Media, XML-Sitemaps und SSL-Verschlüsselung sind bei allen Paketen standardmässig enthalten.",
    },
    {
      q: "Wie funktioniert die Integration von Webhosting und Domain?",
      a: "Da RedWORK über eine eigene Schweizer Hosting-Infrastruktur verfügt, erhalten Sie bei uns alles aus einer Hand. Wir richten Ihre Wunschdomain (.ch, .com etc.), E-Mail-Postfächer und das Hosting im Rechenzentrum Zürich schlüsselfertig für Sie ein.",
    },
    {
      q: "Welche Zahlungsbedingungen gelten für Webdesign-Projekte?",
      a: "In der Regel vereinbaren wir eine Anzahlung von 50% bei Projektstart und die restlichen 50% erst nach erfolgreicher Fertigstellung, Abnahme und Veröffentlichung Ihrer Website. Sie bezahlen bequem per Schweizer QR-Rechnung oder Banküberweisung.",
    },
    {
      q: "Wie lange dauert die Fertigstellung meiner Website?",
      a: "Eine Onepage-Landingpage ist meist in 5 bis 7 Werktagen online. Umfangreichere KMU-Websites benötigen ca. 10 bis 14 Werktage, und Online-Shops oder Web-Apps rund 2 bis 4 Wochen – abhängig von der schnellen Bereitstellung Ihrer Inhalte.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#07090F] text-white flex flex-col selection:bg-[#FF7A00] selection:text-white relative overflow-x-hidden">
      {/* 3D Cinematic Translucent Dynamic Data Counter Background */}
      <CinematicDataCounterBackdrop />

      <Header scrolled={true} />

      <main className="relative z-10 flex-1 pt-24 sm:pt-28 pb-20">
        {/* ================================================== */}
        {/* 1. HERO SECTION                                    */}
        {/* ================================================== */}
        <section className="relative overflow-hidden pt-8 pb-16 sm:pb-20 lg:py-24 border-b border-white/10">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-600/15 via-[#E63946]/10 to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            {/* Swiss Quality Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[#FF7A00]/30 bg-[#FF7A00]/10 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#FF9E40] mb-5 shadow-sm">
              <span className="flex h-1.5 w-1.5 rounded-full bg-[#FF7A00] animate-pulse" />
              <span>🇨🇭 Schweizer Webdesign & Digitale Exzellenz</span>
            </div>

            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl mx-auto leading-tight">
              Moderne Websites, die Besucher in Kunden verwandeln
            </h1>

            <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto mt-4 leading-relaxed font-normal">
              Vom konversionsstarken Onepager bis zum massgeschneiderten Schweizer Online-Shop: Wir
              gestalten, programmieren und hosten Ihren digitalen Erfolg – mobil-perfekt, blitzschnell
              und 100% DSG-konform.
            </p>

            {/* Trust Highlights */}
            <div className="flex items-center justify-center gap-3 sm:gap-5 flex-wrap mt-6 text-xs font-medium text-slate-300">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#FF7A00]" />
                100% Mobile & Tablet Responsive
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#FF7A00]" />
                Google PageSpeed 90+ Score
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#FF7A00]" />
                Schweizer Festpreis-Garantie
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#FF7A00]" />
                Nahtloses Schweizer Hosting
              </span>
            </div>

            {/* Hero CTAs */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 mt-8 flex-wrap">
              <button
                type="button"
                onClick={openQuote}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF7A00] to-[#E63946] hover:from-[#e56d00] hover:to-[#c5303d] text-white font-semibold text-xs sm:text-sm shadow-lg shadow-orange-600/25 transition transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 cursor-pointer"
              >
                <Sparkles size={15} />
                <span>Kostenlose Offerte anfragen</span>
                <ArrowRight size={14} />
              </button>

              <a
                href="#pakete"
                className="w-full sm:w-auto px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium text-xs sm:text-sm border border-white/10 transition inline-flex items-center justify-center gap-2"
              >
                <Layers size={15} />
                <span>Webdesign-Pakete vergleichen</span>
              </a>

              <a
                href="#webview-app"
                className="w-full sm:w-auto px-5 py-3 rounded-xl bg-gradient-to-r from-[#E63946]/15 to-[#FF7A00]/15 hover:from-[#E63946]/25 hover:to-[#FF7A00]/25 text-white font-medium text-xs sm:text-sm border border-[#E63946]/30 transition inline-flex items-center justify-center gap-2"
              >
                <Smartphone size={15} className="text-[#FF7A00]" />
                <span>Website als App (ab CHF 299.-)</span>
              </a>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 2. 3D CINEMATIC WEBVIEW & APP HERO BANNER          */}
        {/* ================================================== */}
        <section
          id="webview-app"
          className="relative py-20 lg:py-28 border-b border-white/10 bg-gradient-to-b from-[#090C14] via-[#0E1322] to-[#07090F] overflow-hidden"
        >
          {/* Ambient Lighting & Cybernetic Grid */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_20%,rgba(230,57,70,0.18),transparent)] pointer-events-none" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Description & Value Props (7 Cols) */}
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#E63946]/30 bg-[#E63946]/10 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#E63946] shadow-sm">
                  <Smartphone size={13} className="animate-pulse" />
                  <span>🇨🇭 Neuheit • Webview & Native App Transformation</span>
                </div>

                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight">
                  Ihre Website als native <span className="bg-gradient-to-r from-[#E63946] via-[#FF7A00] to-[#D4AF37] bg-clip-text text-transparent">iOS & Android App</span>
                </h2>

                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-normal">
                  Bringen Sie Ihr Schweizer Unternehmen direkt auf den Homescreen Ihrer Kunden! Wir konvertieren
                  Ihre bestehende oder neue Website in eine hochperformante native Smartphone-Applikation.
                  Inklusive Push-Benachrichtigungen, Offline-Screen und schlüsselfertiger Bereitstellung für den
                  Apple App Store und Google Play Store.
                </p>

                {/* Core App Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Bell size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">Push-Benachrichtigungen</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Kunden sofort per Push erreichen</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Zap size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">120 FPS Webview 2.0</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Butterweiches Scrolling & native App-Optik</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Download size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">App Store & Play Store</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Icons, Screenshots & Upload-Begleitung</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 bg-white/[0.03] p-2.5 rounded-xl border border-white/5">
                    <div className="h-7 w-7 rounded-lg bg-purple-500/15 text-purple-400 border border-purple-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <ShieldCheck size={14} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">Keine doppelte Pflege</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Änderungen auf der Website sofort in der App</div>
                    </div>
                  </div>
                </div>

                {/* Pricing Callout & Action Bar */}
                <div className="pt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="rounded-xl bg-white/[0.04] p-2.5 px-4 border border-white/10 flex items-baseline gap-2 shrink-0">
                    <span className="text-[11px] text-slate-400 font-medium">Komplettpreis:</span>
                    <span className="text-lg sm:text-xl font-bold font-mono text-[#D4AF37]">Ab CHF 299.-</span>
                    <span className="text-[10px] text-slate-400 font-normal">einmalig</span>
                  </div>

                  <button
                    type="button"
                    onClick={openQuote}
                    className="flex-1 py-3 px-5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#FF7A00] hover:from-[#c5303d] hover:to-[#e56d00] text-white font-semibold text-xs sm:text-sm shadow-lg shadow-red-600/25 transition transform hover:-translate-y-0.5 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Sparkles size={14} />
                    <span>Webview App jetzt unverbindlich anfragen</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>

              {/* Right Column: 3D Cinematic Smartphone Device (5 Cols) */}
              <div className="lg:col-span-5 flex justify-center perspective-[1400px]">
                <div className="relative group transition-all duration-700 ease-out transform lg:rotate-y-[-10deg] lg:rotate-x-[6deg] hover:rotate-0">
                  {/* Outer Glow Halo */}
                  <div className="absolute -inset-4 rounded-[56px] bg-gradient-to-r from-[#E63946]/40 via-[#FF7A00]/30 to-[#D4AF37]/30 blur-2xl opacity-60 group-hover:opacity-100 transition duration-700 pointer-events-none" />

                  {/* 3D Mobile Phone Chassis (Titanium Bezel) */}
                  <div className="relative rounded-[48px] p-3.5 sm:p-4 bg-gradient-to-b from-[#333d52] via-[#1a202c] to-[#0b0e14] border-2 border-white/20 shadow-[0_30px_90px_rgba(0,0,0,0.95)] max-w-[340px] sm:max-w-[380px] w-full">
                    {/* Glossy Reflection Highlight */}
                    <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-white/15 to-transparent rounded-t-[44px] pointer-events-none" />

                    {/* Smartphone Screen Viewport */}
                    <div className="relative rounded-[36px] bg-[#07090F] p-4 text-white overflow-hidden border border-white/10 flex flex-col justify-between min-h-[540px]">
                      {/* Dynamic Island Notch */}
                      <div className="mx-auto w-24 h-5 rounded-full bg-black border border-white/10 flex items-center justify-between px-2.5 mb-2 shadow-inner">
                        <span className="h-2 w-2 rounded-full bg-[#111] border border-white/10" />
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                      </div>

                      {/* Top Status Bar */}
                      <div className="flex items-center justify-between text-[10px] font-mono text-slate-300 pb-3 border-b border-white/10">
                        <span className="font-bold">09:41</span>
                        <div className="flex items-center gap-1.5">
                          <span>5G 🇨🇭</span>
                          <Wifi size={11} />
                          <BatteryCharging size={13} className="text-emerald-400" />
                        </div>
                      </div>

                      {/* Prominent RedWORK App Header */}
                      <div className="py-3 flex items-center justify-between">
                        <div>
                          <div className="text-xl font-black tracking-wider text-white">
                            Red<span className="text-[#E63946]">WORK</span>
                          </div>
                          <div className="text-[10px] font-mono text-slate-400">Swiss Digital & Hosting App</div>
                        </div>
                        <span className="text-[9px] font-mono font-black uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping" />
                          ONLINE
                        </span>
                      </div>

                      {/* Interactive Push Notification Banner Simulation */}
                      <div className="p-3 rounded-2xl bg-gradient-to-r from-[#182033] to-[#121826] border border-cyan-500/40 shadow-lg text-left my-2 animate-pulse">
                        <div className="flex items-center gap-2 mb-1">
                          <Bell size={13} className="text-cyan-400" />
                          <span className="text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-wider">
                            Push-Mitteilung (Neu)
                          </span>
                        </div>
                        <p className="text-xs font-bold text-white leading-tight">
                          Ihr Webauftritt ist jetzt live als iOS & Android App verfügbar! 🚀
                        </p>
                        <span className="text-[9px] text-slate-400 block mt-1 font-mono">vor 2 Minuten • RedWORK Cloud</span>
                      </div>

                      {/* Webview Simulated Content Grid */}
                      <div className="space-y-2 py-2">
                        <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-emerald-400" />
                            <span className="text-xs font-bold text-slate-200">NVMe High-Speed Webspace</span>
                          </div>
                          <span className="text-[10px] font-mono text-emerald-400 font-bold">10 Gbit/s</span>
                        </div>

                        <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-[#E63946]" />
                            <span className="text-xs font-bold text-slate-200">Zürich Tier-IV Datacenter</span>
                          </div>
                          <span className="text-[10px] font-mono text-slate-300">DSG Konform</span>
                        </div>

                        <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
                            <span className="text-xs font-bold text-slate-200">Schweizer Support-Desk</span>
                          </div>
                          <span className="text-[10px] font-mono text-amber-300 font-bold">24/7 Live</span>
                        </div>
                      </div>

                      {/* App Store & Google Play Badges inside screen footer */}
                      <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[10px] font-mono text-slate-400">
                        <div className="flex items-center gap-1.5">
                          <span className="px-2 py-1 rounded-lg bg-white/10 text-white font-bold text-[9px] border border-white/10">
                            🍎 App Store
                          </span>
                          <span className="px-2 py-1 rounded-lg bg-white/10 text-white font-bold text-[9px] border border-white/10">
                            🤖 Google Play
                          </span>
                        </div>
                        <span className="font-mono text-emerald-400 font-bold">v2.4 Ready</span>
                      </div>

                      {/* Home Indicator Bar */}
                      <div className="mx-auto w-28 h-1 rounded-full bg-white/30 mt-3" />
                    </div>

                    {/* Floating 3D Badge on side */}
                    <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-[#E63946] to-[#FF7A00] text-white p-2 px-3 rounded-2xl shadow-xl border border-white/20 text-xs font-mono font-black flex items-center gap-1.5">
                      <Sparkles size={13} />
                      <span>CHF 299.- FESTPREIS</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 3. TRANSPARENTE SCHWEIZER FESTPREISE & PAKETE      */}
        {/* ================================================== */}
        <section id="pakete" className="py-16 sm:py-20 lg:py-24 border-b border-white/10 bg-gradient-to-b from-[#090C14] via-[#0D1220] to-[#07090F] relative overflow-hidden">
          {/* Subtle Ambient Backlighting */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(255,122,0,0.08),transparent)] pointer-events-none" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Refined Header Section (Elegant, Modern, Nicht Kaba) */}
            <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[#FF7A00]/30 bg-[#FF7A00]/10 px-3.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#FF9E40] mb-3 shadow-sm">
                <Sparkles size={12} className="animate-pulse" />
                <span>Schweizer Festpreise • Ohne Zusatzkosten</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
                Wählen Sie das passende{" "}
                <span className="bg-gradient-to-r from-[#FF7A00] via-[#E63946] to-[#FFC107] bg-clip-text text-transparent">
                  Webdesign-Paket
                </span>
              </h2>

              <p className="text-xs sm:text-sm text-slate-300 mt-2.5 max-w-xl mx-auto leading-relaxed font-normal">
                Erstklassige Schweizer Web-Entwicklung zu transparenten Konditionen. Alle Pakete sind verbindliche Festpreise ohne versteckte Kosten oder böse Überraschungen.
              </p>

              {/* Value Guarantee Badges */}
              <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap mt-4 text-[11px] font-medium text-slate-300">
                <span className="flex items-center gap-1 bg-white/[0.03] px-2.5 py-1 rounded-full border border-white/10">
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  Festpreis-Garantie
                </span>
                <span className="flex items-center gap-1 bg-white/[0.03] px-2.5 py-1 rounded-full border border-white/10">
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  100% Mobile & Tablet Optimiert
                </span>
                <span className="flex items-center gap-1 bg-white/[0.03] px-2.5 py-1 rounded-full border border-white/10">
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  Max. CHF 2'990.- Budget-Limit
                </span>
              </div>
            </div>

            {/* Filter Pills (Refined, Mobile-Friendly) */}
            <div className="flex items-center justify-start sm:justify-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar pb-3 max-w-5xl mx-auto px-1 mb-8 sm:mb-12">
              <button
                type="button"
                onClick={() => setActiveTab("all")}
                className={`px-3.5 py-1.5 sm:py-2 rounded-xl text-xs font-medium transition shrink-0 whitespace-nowrap cursor-pointer border ${
                  activeTab === "all"
                    ? "bg-gradient-to-r from-[#FF7A00] to-[#E63946] text-white shadow-md shadow-orange-600/25 border-[#FF7A00]"
                    : "bg-[#0C101A] text-slate-300 border-white/10 hover:border-white/20 hover:text-white"
                }`}
              >
                Alle Pakete ({WEBDESIGN_PACKAGES.length})
              </button>
              {WEBDESIGN_PACKAGES.map((pkg) => (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => setActiveTab(pkg.id)}
                  className={`px-3.5 py-1.5 sm:py-2 rounded-xl text-xs font-medium transition shrink-0 whitespace-nowrap cursor-pointer border ${
                    activeTab === pkg.id
                      ? "bg-gradient-to-r from-[#FF7A00] to-[#E63946] text-white shadow-md shadow-orange-600/25 border-[#FF7A00]"
                      : "bg-[#0C101A] text-slate-300 border-white/10 hover:border-white/20 hover:text-white"
                  }`}
                >
                  {pkg.name}
                </button>
              ))}
            </div>

            {/* Packages Grid - Refined Modern Proportions */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 items-stretch">
              {filteredPackages.map((pkg) => {
                const IconComponent = pkg.icon;
                const isFeatured = pkg.isFeatured;

                return (
                  <div
                    key={pkg.id}
                    id={pkg.id}
                    className={`relative flex flex-col justify-between rounded-2xl p-5 sm:p-5 lg:p-6 transition-all duration-300 hover:-translate-y-1 min-w-0 ${
                      isFeatured
                        ? "bg-gradient-to-b from-[#1A1624] via-[#111420] to-[#0A0D16] border border-[#FF7A00]/70 shadow-[0_15px_40px_rgba(255,122,0,0.18)]"
                        : "bg-gradient-to-b from-[#0F1420] to-[#0A0D16] border border-white/10 shadow-md hover:border-white/20"
                    }`}
                  >
                    {/* Bestseller Subtle Top Badge */}
                    {isFeatured && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                        <span className="rounded-full px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-gradient-to-r from-[#FF7A00] to-[#E63946] text-white shadow-sm border border-white/20">
                          ★ Bestseller KMU
                        </span>
                      </div>
                    )}

                    {/* Badge & Delivery Time */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${pkg.badgeClass}`}
                      >
                        {pkg.badge}
                      </span>
                      <span className="text-[10.5px] font-mono text-slate-400 flex items-center gap-1 bg-white/[0.03] px-2 py-0.5 rounded-md border border-white/5">
                        <Clock size={11} className="text-[#FF7A00]" />
                        <span>{pkg.deliveryTime}</span>
                      </span>
                    </div>

                    <div>
                      <div className="flex items-center gap-2.5 mb-2">
                        <div
                          className={`flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 border border-white/10 ${pkg.iconColor} shadow-inner shrink-0`}
                        >
                          <IconComponent size={18} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[9.5px] uppercase tracking-wider text-slate-400 font-medium">
                            {pkg.category}
                          </p>
                          <h3 className="text-sm sm:text-base font-bold text-white break-words leading-snug">
                            {pkg.name}
                          </h3>
                        </div>
                      </div>

                      <p className="text-xs text-slate-400 min-h-[32px] leading-relaxed mb-3.5 break-words font-normal">
                        {pkg.shortDesc}
                      </p>

                      {/* Refined Price Display Box (Sleek, Elegant, Nicht Kaba) */}
                      <div className="rounded-xl bg-white/[0.025] hover:bg-white/[0.04] p-3 sm:p-3.5 border border-white/10 transition mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                            Schweizer Festpreis:
                          </span>
                          <span className="text-[9px] text-emerald-400 font-medium bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                            Fixpreis
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-xs font-normal text-slate-400">CHF</span>
                          <span className="text-xl sm:text-2xl font-bold font-mono tracking-tight text-white group-hover:text-[#FF9E40] transition">
                            {pkg.price ? pkg.price.toLocaleString("de-CH") : pkg.priceLabel}
                          </span>
                          <span className="text-xs font-normal text-slate-400 ml-0.5">
                            {pkg.id === "wartung" ? "/ Mt." : ".-"}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1 font-normal">
                          Inkl. Konzeption, Design & Schweizer Aufschaltung
                        </p>
                      </div>

                      {/* Feature Bullet List (Refined Typography) */}
                      <div className="space-y-1.5 mb-5">
                        <p className="text-[9.5px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                          Enthaltene Leistungen:
                        </p>
                        {pkg.features.map((feat, fIdx) => (
                          <div key={fIdx} className="flex items-start gap-2 text-[11.5px] sm:text-xs text-slate-300 min-w-0 leading-relaxed font-normal">
                            <CheckCircle2
                              size={12}
                              className={`shrink-0 mt-0.5 ${
                                isFeatured ? "text-[#FF7A00]" : "text-emerald-400"
                              }`}
                            />
                            <span className="break-words">{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-3 border-t border-white/10 space-y-1.5">
                      <button
                        type="button"
                        onClick={openQuote}
                        className={`w-full py-2.5 px-4 rounded-xl font-semibold text-xs sm:text-sm flex items-center justify-center gap-2 transition shadow-md cursor-pointer ${
                          isFeatured
                            ? "bg-gradient-to-r from-[#FF7A00] to-[#E63946] hover:from-[#e56d00] hover:to-[#c5303d] text-white shadow-orange-600/25"
                            : "bg-white text-slate-900 hover:bg-slate-100"
                        }`}
                      >
                        <Sparkles size={13} />
                        <span>Offerte jetzt anfordern</span>
                        <ArrowRight size={13} />
                      </button>

                      <button
                        type="button"
                        onClick={openContact}
                        className="w-full py-1.5 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] font-normal text-slate-400 hover:text-white transition text-center border border-white/5 cursor-pointer"
                      >
                        Frage zu diesem Paket stellen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 4. ZUSATZLEISTUNGEN & POWER-ADDONS (EXTRALAR)      */}
        {/* ================================================== */}
        <section id="extralar" className="py-16 sm:py-20 lg:py-24 border-b border-white/10 bg-[#090C14]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#FF7A00] bg-[#FF7A00]/10 px-3.5 py-1 rounded-full border border-[#FF7A00]/25 inline-flex items-center gap-1.5 mb-2.5">
                <Sparkles size={12} className="animate-pulse" />
                <span>Extralar & Modulare Schweizer Power-Addons</span>
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-1">
                Zusatzleistungen für Ihren digitalen Vorsprung
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 mt-2 max-w-xl mx-auto leading-relaxed font-normal">
                Kombinieren Sie Ihre Website mit massgeschneiderten Schweizer Service-Paketen.
                Vom Express-Launch bis zur DSG-Rechtssicherheit – flexibel zubuchbar.
              </p>
            </div>

            {/* Add-ons 4-Column Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 items-stretch">
              {WEBDESIGN_ADDONS.map((addon) => {
                const AddonIcon = addon.icon;
                return (
                  <div
                    key={addon.id}
                    className="relative flex flex-col justify-between rounded-2xl p-5 bg-gradient-to-b from-[#0F1420] to-[#0A0D16] border border-white/10 hover:border-white/20 shadow-md transition-all duration-300 hover:-translate-y-1"
                  >
                    <div>
                      {/* Top Row: Icon + Badge */}
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 border border-white/10 text-[#FF7A00] shadow-inner">
                          <AddonIcon size={18} />
                        </div>
                        <span
                          className={`text-[8.5px] font-semibold uppercase px-2 py-0.5 rounded-full border tracking-wider ${addon.badgeColor}`}
                        >
                          {addon.badge}
                        </span>
                      </div>

                      <h3 className="text-sm font-semibold text-white mb-1.5 leading-snug">
                        {addon.name}
                      </h3>

                      <p className="text-xs text-slate-400 min-h-[38px] leading-relaxed mb-3 font-normal">
                        {addon.desc}
                      </p>

                      {/* Pricing Tag */}
                      <div className="rounded-xl bg-white/[0.03] p-2.5 border border-white/5 mb-3.5 flex items-baseline justify-between">
                        <span className="text-[10px] uppercase font-medium text-slate-400">Preis:</span>
                        <div className="text-right">
                          <span className="font-mono text-sm sm:text-base font-bold text-[#D4AF37]">
                            {addon.price}
                          </span>
                          <span className="text-[9.5px] text-slate-400 block -mt-0.5">{addon.priceSub}</span>
                        </div>
                      </div>

                      {/* Features Checkmarks */}
                      <div className="space-y-1.5 mb-5">
                        {addon.features.map((feat, fIdx) => (
                          <div key={fIdx} className="flex items-start gap-1.5 text-[11px] text-slate-300 font-normal leading-relaxed">
                            <CheckCircle2 size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      type="button"
                      onClick={openQuote}
                      className="w-full py-2 px-3 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-medium text-slate-300 hover:text-white border border-white/10 hover:border-white/20 transition flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <span>Addon anfragen</span>
                      <ArrowRight size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 6. WORKFLOW / SCHRITTE ZUR NEUEN WEBSITE           */}
        {/* ================================================== */}
        <section className="py-16 sm:py-20 lg:py-24 border-b border-white/10 bg-[#090C14]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#FF9E40]">
                Strukturierter Schweizer Projektablauf
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1.5">
                In 4 Schritten zu Ihrem erfolgreichen Webauftritt
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 mt-2 font-normal">
                Transparente Kommunikation, feste Meilensteine und garantierte Termintreue.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              <div className="bg-[#0F1420] p-5 rounded-2xl border border-white/10 relative">
                <div className="text-2xl font-bold text-[#FF7A00]/30 font-mono mb-1.5">01</div>
                <h4 className="text-sm font-semibold text-white mb-1.5">Briefing & Konzept</h4>
                <p className="text-xs text-slate-400 leading-relaxed font-normal">
                  Gemeinsame Analyse Ihrer Wünsche, Zielgruppen und Definition der Seitenstruktur
                  (Sitemap).
                </p>
              </div>

              <div className="bg-[#0F1420] p-5 rounded-2xl border border-white/10 relative">
                <div className="text-2xl font-bold text-[#FF7A00]/30 font-mono mb-1.5">02</div>
                <h4 className="text-sm font-semibold text-white mb-1.5">UI/UX Designentwurf</h4>
                <p className="text-xs text-slate-400 leading-relaxed font-normal">
                  Erstellung eines individuellen Design-Entwurfs auf Desktop & Mobile. Anpassungen nach
                  Ihrem Feedback.
                </p>
              </div>

              <div className="bg-[#0F1420] p-5 rounded-2xl border border-white/10 relative">
                <div className="text-2xl font-bold text-[#FF7A00]/30 font-mono mb-1.5">03</div>
                <h4 className="text-sm font-semibold text-white mb-1.5">Entwicklung & CMS</h4>
                <p className="text-xs text-slate-400 leading-relaxed font-normal">
                  Pixelgenaue Umsetzung, SEO-Optimierung, Mobile-Testing und Integration von cPanel /
                  Schweizer Hosting.
                </p>
              </div>

              <div className="bg-[#0F1420] p-5 rounded-2xl border border-white/10 relative">
                <div className="text-2xl font-bold text-emerald-500/30 font-mono mb-1.5">04</div>
                <h4 className="text-sm font-semibold text-white mb-1.5">Testing & Go-Live</h4>
                <p className="text-xs text-slate-400 leading-relaxed font-normal">
                  Abschliessende Qualitätsprüfung, Aufschaltung der Domain und Schulung für Sie und Ihr
                  Team.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 7. FAQS ACCORDION                                  */}
        {/* ================================================== */}
        <section className="py-16 sm:py-20 border-b border-white/10">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#FF9E40]">
                Häufige Fragen
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">
                Wissenswertes zu unseren Webdesign-Dienstleistungen
              </h2>
            </div>

            <div className="space-y-2.5">
              {faqs.map((faq, idx) => {
                const isOpen = activeFaq === idx;
                return (
                  <div
                    key={idx}
                    className="rounded-xl border border-white/10 bg-[#0F1420] overflow-hidden transition"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveFaq(isOpen ? null : idx)}
                      className="w-full p-4 text-left flex items-center justify-between gap-3 font-semibold text-xs sm:text-sm text-white hover:text-[#FF9E40] transition cursor-pointer"
                    >
                      <span>{faq.q}</span>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-[#FF9E40]" : "text-slate-400"
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-xs text-slate-300 leading-relaxed border-t border-white/5 pt-2.5 font-normal">
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================================================== */}
        {/* 8. CONVERSION CTA                                  */}
        {/* ================================================== */}
        <section className="py-16 sm:py-20 text-center">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
              Starten Sie Ihr Webprojekt mit RedWORK
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 mt-3 max-w-xl mx-auto leading-relaxed font-normal">
              Lassen Sie sich kostenlos und unverbindlich beraten. Wir erstellen Ihnen innerhalb von
              24 Stunden eine transparente Schweizer Festpreis-Offerte.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={openQuote}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF7A00] to-[#E63946] hover:from-[#e56d00] hover:to-[#c5303d] text-white font-semibold text-xs sm:text-sm shadow-lg shadow-orange-600/25 transition transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 cursor-pointer"
              >
                <Sparkles size={15} />
                <span>Offerte jetzt online anfordern</span>
                <ArrowRight size={14} />
              </button>

              <button
                type="button"
                onClick={openContact}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium text-xs sm:text-sm border border-white/10 transition inline-flex items-center justify-center gap-2 cursor-pointer"
              >
                <PhoneCall size={15} />
                <span>Telefonische Beratung vereinbaren</span>
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
